import React, { useRef, useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Rocket, Play, CheckCircle2, XCircle, CircleDashed, Loader2, Sparkles, Share2, Link as LinkIcon, Download, PartyPopper, Trophy } from "lucide-react";
import confetti from "canvas-confetti";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS = {
  ready: { label: "Ready to ship", emoji: "🚀", color: "#10b981" },
  almost: { label: "Almost there", emoji: "⏳", color: "#f59e0b" },
  "not-yet": { label: "In development", emoji: "🔧", color: "#f43f5e" },
};

// Public, unauthenticated release page — anyone with the link sees the game's release scorecard and
// can jump into the public play page. When a game passes the gate ("Ready to ship") it fires a
// confetti celebration with a shareable, downloadable badge. Standalone (no app chrome, no auth).
export default function ReleasePage() {
  const { id } = useParams();
  const ringRef = useRef(null);
  const badgeRef = useRef(null);
  const firedRef = useRef(false);
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | missing
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/api/og/release/${id}`;

  useEffect(() => {
    fetch(`${BACKEND}/api/compiler/${id}/release/public`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { setData(d); setState("ok"); })
      .catch(() => setState("missing"));
  }, [id]);

  const rel = data?.release;
  const st = rel ? (STATUS[rel.status] || STATUS["not-yet"]) : null;
  const isReady = rel?.status === "ready";

  // Readiness ring
  useEffect(() => {
    const c = ringRef.current;
    if (!c || !rel) return;
    const ctx = c.getContext("2d");
    const W = 240, H = 240, cx = W / 2, cy = H / 2, r = 96;
    ctx.clearRect(0, 0, W, H);
    ctx.lineWidth = 22; ctx.lineCap = "round";
    ctx.strokeStyle = "rgba(255,255,255,0.10)";
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = st.color;
    ctx.beginPath(); ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * (rel.readiness / 100)); ctx.stroke();
    ctx.fillStyle = st.color; ctx.font = "bold 62px Inter, system-ui, sans-serif";
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText(`${rel.readiness}`, cx, cy - 8);
    ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = "16px Inter, system-ui, sans-serif";
    ctx.fillText("release-ready", cx, cy + 34);
  }, [rel, st]);

  // Confetti celebration when a game is Ready to ship
  useEffect(() => {
    if (!isReady || firedRef.current) return;
    firedRef.current = true;
    const t = setTimeout(() => {
      const fire = (ratio, opts) => confetti({ particleCount: Math.floor(240 * ratio), spread: 95, origin: { y: 0.4 }, colors: ["#10b981", "#38bdf8", "#a855f7", "#fbbf24"], ...opts });
      fire(0.3, { startVelocity: 62 });
      fire(0.25, { spread: 120, decay: 0.92, scalar: 1.1 });
      setTimeout(() => fire(0.35, { spread: 130, startVelocity: 45 }), 240);
    }, 350);
    return () => clearTimeout(t);
  }, [isReady]);

  // Shareable "Ready to Ship" badge (square emblem)
  const drawBadge = useCallback(() => {
    const c = badgeRef.current;
    if (!c || !rel) return;
    const ctx = c.getContext("2d");
    const S = 640, cx = S / 2, cy = 268, r = 150;
    const grad = ctx.createLinearGradient(0, 0, S, S);
    grad.addColorStop(0, "#04070f"); grad.addColorStop(1, st.color);
    ctx.fillStyle = grad; ctx.fillRect(0, 0, S, S);
    ctx.fillStyle = "rgba(4,7,15,0.5)"; ctx.fillRect(0, 0, S, S);
    ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.font = "bold 24px Inter, system-ui, sans-serif";
    ctx.fillText("GAMEVIEW STUDIO", cx, 78);
    ctx.lineWidth = 28; ctx.lineCap = "round";
    ctx.strokeStyle = "rgba(255,255,255,0.12)"; ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = st.color; ctx.beginPath(); ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * (rel.readiness / 100)); ctx.stroke();
    ctx.textBaseline = "middle";
    ctx.font = "72px Inter, system-ui, sans-serif"; ctx.fillStyle = "#fff"; ctx.fillText(st.emoji, cx, cy - 34);
    ctx.font = "bold 76px Inter, system-ui, sans-serif"; ctx.fillStyle = st.color; ctx.fillText(`${rel.readiness}%`, cx, cy + 44);
    ctx.textBaseline = "alphabetic";
    ctx.font = "bold 46px Inter, system-ui, sans-serif"; ctx.fillStyle = "#fff"; ctx.fillText(st.label.toUpperCase(), cx, 480);
    const title = (data.title || "My Game");
    ctx.font = "28px Inter, system-ui, sans-serif"; ctx.fillStyle = "rgba(255,255,255,0.82)";
    ctx.fillText(title.length > 30 ? title.slice(0, 29) + "…" : title, cx, 528);
    ctx.font = "22px Inter, system-ui, sans-serif"; ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.fillText("Built & shipped with AI", cx, 590);
  }, [rel, st, data]);

  useEffect(() => { if (state === "ok" && isReady) drawBadge(); }, [state, isReady, drawBadge]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* noop */ }
  };
  const nativeShare = async () => {
    try { await navigator.share({ title: data?.title || "GameView", text: `"${data?.title}" is ${st?.label} (${rel?.readiness}% release-ready) — come play it!`, url: shareUrl }); } catch { /* cancelled */ }
  };
  const downloadBadge = () => {
    const c = badgeRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "ready-to-ship-badge.png"; a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  return (
    <div className="min-h-screen bg-[#05070f] text-white" style={{ backgroundImage: "radial-gradient(1200px 600px at 50% -10%, rgba(56,189,248,0.18), transparent 60%)" }} data-testid="release-page">
      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-white/80 hover:text-white transition-colors" data-testid="release-home">
            <Sparkles className="w-4 h-4 text-[#38bdf8]" /> GameView Studio
          </Link>
          <Link to="/leaderboard" className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/60 hover:text-white transition-colors" data-testid="release-leaderboard-link">
            <Trophy className="w-3.5 h-3.5 text-amber-400" /> Leaderboard
          </Link>
        </div>

        {state === "loading" && <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-white/50" /></div>}

        {state === "missing" && (
          <div className="py-24 text-center" data-testid="release-missing">
            <div className="text-5xl mb-3">🎮</div>
            <h1 className="text-2xl font-extrabold mb-2">This game hasn't been shipped yet</h1>
            <p className="text-white/60 mb-6">The creator hasn't published a release scorecard for it.</p>
            <Link to="/" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-bold bg-gradient-to-r from-[#0ea5e9] to-[#7c3aed]">Make your own game</Link>
          </div>
        )}

        {state === "ok" && rel && (
          <div className="mt-8">
            {/* Ready-to-ship celebration */}
            {isReady && (
              <div className="mb-8 rounded-3xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/15 via-[#0a1830]/40 to-[#38bdf8]/10 p-6 sm:p-7 animate-in fade-in slide-in-from-top-2 duration-500" data-testid="release-celebration">
                <div className="flex items-center gap-2 text-emerald-300 font-extrabold text-sm uppercase tracking-widest mb-3">
                  <PartyPopper className="w-5 h-5" /> Ready to ship!
                </div>
                <div className="grid sm:grid-cols-[168px_1fr] gap-5 items-center">
                  <div className="grid place-items-center">
                    <canvas ref={badgeRef} width={640} height={640} className="w-[150px] h-[150px] rounded-2xl border border-white/10 shadow-2xl" data-testid="release-badge" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-extrabold leading-tight mb-1.5">This game passed the release gate 🎉</h2>
                    <p className="text-white/65 text-sm leading-snug mb-4">Grab your shareable badge and drop the link anywhere — it unfurls into a rich preview.</p>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={downloadBadge} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold bg-gradient-to-r from-[#10b981] to-[#0ea5e9] hover:opacity-95 transition-opacity" data-testid="release-badge-download">
                        <Download className="w-4 h-4" /> Save badge
                      </button>
                      <button onClick={copyLink} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold border border-white/20 hover:border-white/40 transition-colors" data-testid="release-copy-link">
                        <LinkIcon className="w-4 h-4" /> {copied ? "Copied!" : "Copy link"}
                      </button>
                      {typeof navigator !== "undefined" && navigator.share && (
                        <button onClick={nativeShare} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold border border-white/20 hover:border-white/40 transition-colors" data-testid="release-native-share">
                          <Share2 className="w-4 h-4" /> Share…
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/50 mb-2">
              <Rocket className="w-4 h-4" style={{ color: st.color }} /> Release Card
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight" data-testid="release-title">{data.title}</h1>
            {data.genre && <p className="text-white/55 mt-1 text-lg">{data.genre}</p>}

            <div className="mt-8 grid sm:grid-cols-[240px_1fr] gap-8 items-center">
              <div className="grid place-items-center">
                <canvas ref={ringRef} width={240} height={240} className="w-[200px] h-[200px]" data-testid="release-ring" />
              </div>
              <div>
                <div className="text-2xl font-extrabold mb-4" style={{ color: st.color }} data-testid="release-status">{st.emoji} {st.label}</div>
                <div className="space-y-2">
                  {rel.checklist.map((c) => (
                    <div key={c.key} className="flex items-start gap-2.5" data-testid={`release-check-${c.key}`}>
                      {c.pass ? <CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-400 shrink-0" />
                        : c.incomplete ? <CircleDashed className="w-5 h-5 mt-0.5 text-white/40 shrink-0" />
                          : <XCircle className="w-5 h-5 mt-0.5 text-rose-400 shrink-0" />}
                      <div>
                        <div className="text-sm font-semibold">{c.label}</div>
                        <div className="text-xs text-white/50">{c.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to={data.play_url || `/g/${id}`} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-bold text-lg bg-gradient-to-r from-[#0ea5e9] to-[#10b981] hover:opacity-95 transition-opacity" data-testid="release-play">
                <Play className="w-5 h-5" /> Play now
              </Link>
              {!isReady && (
                <button onClick={copyLink} className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-bold border border-white/20 hover:border-white/40 transition-colors" data-testid="release-copy-link">
                  <LinkIcon className="w-5 h-5" /> {copied ? "Copied!" : "Copy link"}
                </button>
              )}
              <Link to="/" className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-bold border border-white/20 hover:border-white/40 transition-colors" data-testid="release-cta">
                Make your own
              </Link>
            </div>
            <p className="text-white/35 text-xs mt-6">Built with GameView Studio · the AI game compiler.</p>
          </div>
        )}
      </div>
    </div>
  );
}
